import { createParser } from 'nuqs';

import { parseAdvancedFilterDefinition, parseAdvancedFilterDefinitions } from './advancedFilters';
import { isRecord } from './jsonLogic';
import {
    buildSearchFilterEnvelope,
    getSearchAdvancedFilter,
    getSearchAdvancedFilters,
    getSearchFilterJoin,
    getSearchFilterOperators,
    isSearchFilterEnvelope,
    unwrapSearchFilters,
    type SearchAdvancedFilterRule,
    type SearchFilterJoin,
    type SearchFilterOperator,
    type SearchFiltersValue,
} from './searchFilters';

const JOIN_KEY = '$join';
const OPERATORS_KEY = '$operators';
const ADVANCED_KEY = '$advanced';

export type SearchFiltersQueryValue = SearchFiltersValue | ReturnType<typeof buildSearchFilterEnvelope>;

const parseJoin = (value: unknown): SearchFilterJoin => (value === 'all' ? 'all' : 'any');

const parseOperators = (value: unknown): Record<string, SearchFilterOperator> => {
    if (!isRecord(value)) {
        return {};
    }

    const validOperators = new Set<SearchFilterOperator>([
        'contains',
        'equals',
        'greaterThan',
        'lessThan',
        'notEquals',
    ]);

    return Object.fromEntries(
        Object.entries(value).filter(
            (entry): entry is [string, SearchFilterOperator] =>
                typeof entry[1] === 'string' && validOperators.has(entry[1] as SearchFilterOperator),
        ),
    );
};

const isSearchAdvancedRule = (value: unknown): value is SearchAdvancedFilterRule =>
    isRecord(value) &&
    typeof value.key === 'string' &&
    typeof value.path === 'string' &&
    'value' in value &&
    typeof value.operator === 'string' &&
    ['contains', 'equals', 'greaterThan', 'lessThan', 'notEquals'].includes(value.operator) &&
    (value.sourcePath === undefined ||
        (Array.isArray(value.sourcePath) && value.sourcePath.every((part) => typeof part === 'string'))) &&
    (value.group === undefined || value.group === 'eventFilters' || value.group === 'premiumFilters');

const normalizeLegacySearchFilterState = (value: Record<string, unknown>) =>
    Object.fromEntries(
        Object.entries(value).filter(([key]) => key !== JOIN_KEY && key !== OPERATORS_KEY && key !== ADVANCED_KEY),
    );

export const parseSearchFiltersQueryValue = (value: string): SearchFiltersQueryValue | null => {
    try {
        const parsedValue: unknown = JSON.parse(value);

        if (!isRecord(parsedValue)) {
            return null;
        }

        if (isSearchFilterEnvelope(parsedValue)) {
            return buildSearchFilterEnvelope(
                unwrapSearchFilters(parsedValue),
                getSearchFilterJoin(parsedValue),
                getSearchFilterOperators(parsedValue),
                getSearchAdvancedFilters(parsedValue),
            );
        }

        // Migrate older envelope-shaped URLs even when their schema version predates
        // the currently supported envelope discriminator.
        if (isRecord(parsedValue.legacy) && ('jsonLogic' in parsedValue || 'schemaVersion' in parsedValue)) {
            const metadata = isRecord(parsedValue.metadata) ? parsedValue.metadata : {};

            return buildSearchFilterEnvelope(
                parsedValue.legacy,
                parseJoin(metadata.matchingPattern),
                parseOperators(metadata.operators),
                parseAdvancedFilterDefinitions(parsedValue.advancedFilters, isSearchAdvancedRule).length
                    ? parseAdvancedFilterDefinitions(parsedValue.advancedFilters, isSearchAdvancedRule)
                    : [parseAdvancedFilterDefinition(parsedValue.advancedFilter, isSearchAdvancedRule)].filter(
                          (definition): definition is NonNullable<typeof definition> => Boolean(definition),
                      ),
            );
        }

        return buildSearchFilterEnvelope(
            normalizeLegacySearchFilterState(parsedValue),
            parseJoin(parsedValue[JOIN_KEY]),
            parseOperators(parsedValue[OPERATORS_KEY]),
            Array.isArray(parsedValue[ADVANCED_KEY])
                ? parseAdvancedFilterDefinitions(parsedValue[ADVANCED_KEY], isSearchAdvancedRule)
                : [parseAdvancedFilterDefinition(parsedValue[ADVANCED_KEY], isSearchAdvancedRule)].filter(
                      (definition): definition is NonNullable<typeof definition> => Boolean(definition),
                  ),
        );
    } catch {
        return null;
    }
};

export const serializeSearchFiltersQueryValue = (value: SearchFiltersQueryValue): string => {
    const filters = unwrapSearchFilters(value);
    const join = getSearchFilterJoin(value);
    const operators = getSearchFilterOperators(value);
    const advancedFilters = getSearchAdvancedFilters(value);

    return JSON.stringify({
        ...filters,
        ...(join === 'all' ? { [JOIN_KEY]: join } : {}),
        ...(Object.keys(operators).length ? { [OPERATORS_KEY]: operators } : {}),
        ...(advancedFilters.length ? { [ADVANCED_KEY]: advancedFilters } : {}),
    });
};

export const searchFiltersQuery = createParser<SearchFiltersQueryValue>({
    eq: (left, right) => serializeSearchFiltersQueryValue(left) === serializeSearchFiltersQueryValue(right),
    parse: parseSearchFiltersQueryValue,
    serialize: serializeSearchFiltersQueryValue,
}).withDefault({});
